import { forwardRef, useState, useImperativeHandle, useCallback, useMemo } from 'react';
import { Drawer } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import { useSelector } from 'react-redux';
import { Button, Dialog } from 'antd-mobile';
import { isNewSoftwareVersion } from '@/utils';
import { useShowTime } from '@/hooks';
import { SocketClientType } from '@/service/socketio';
import { BODY_PART, storagePrefixV2 } from '@/constant';
import { selectCurrentServingOrder } from '@/store/service';
import cx from './index.module.scss';

enum IMAGE_SRC {
  Shoulder = storagePrefixV2 + '/exposed-area-image/shoulder-exposed-area-2.png',
  Abdomen = storagePrefixV2 + '/exposed-area-image/abdomen-exposed-area-2.png',
  Waist = storagePrefixV2 + '/exposed-area-image/waist-exposed-area-2.png',
}

// type ExposedAreaCodeType = 'Shoulder' | 'Abdomen' | 'Waist';
interface Props {
  exposedAreaCode: MassageRuntime.RobotExceptionEvents;
  socketInstance: React.MutableRefObject<SocketClientType>;
}

export interface PhysicalTherapyDrawerRefAttrs {
  show: VoidFunc;
  hide: VoidFunc;
}

const PhysicalTherapyDrawer = forwardRef<PhysicalTherapyDrawerRefAttrs, Props>(
  ({ exposedAreaCode = 'LIMITED_ABDOMEN_EXPOSED_AREA', socketInstance }, ref) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const currentServingOrder = useSelector(selectCurrentServingOrder);

    const currentExposedAreaCodeType = useMemo(() => {
      if (exposedAreaCode === 'ABDOMEN_FALSE_AREA' || exposedAreaCode === 'LIMITED_ABDOMEN_EXPOSED_AREA') {
        return 'Abdomen';
      } else if (exposedAreaCode === 'LIMITED_SHOULDER_EXPOSED_AREA') {
        return 'Shoulder';
      } else if (exposedAreaCode === 'LIMITED_WAIST_EXPOSED_AREA') {
        return 'Waist';
      }
    }, [exposedAreaCode]);

    const time = useShowTime('星期dd&HH:mm');

    const toggleShow = useCallback(
      (flag: boolean) => () => {
        setIsDrawerOpen(flag);
      },
      []
    );
    const handleForceClose = useCallback(() => {
      Dialog.confirm({
        title: '确认要跳过理疗区域检测吗？',
        content: (
          <div>
            为保证用户的理疗体验，
            <br />
            请确认毛巾已按照要求遮挡好。
          </div>
        ),
        confirmText: '确认',
        onConfirm: async () => {
          try {
            await socketInstance?.current?.skipBareAreaAbnormal();
          } catch (e) {
            console.error(e);
          }
        },
      });
    }, [socketInstance]);

    useImperativeHandle(
      ref,
      () => ({
        show: toggleShow(true),
        hide: toggleShow(false),
      }),
      [toggleShow]
    );

    return (
      <Drawer
        placement="right"
        width={'520px'}
        className={cx('video-drawer')}
        styles={{
          header: { border: 'none' },
          body: { background: 'linear-gradient(180deg, #BBC2D0 0%, #E6E8EB 100%)' },
        }}
        closable={false}
        destroyOnClose={true}
        open={isDrawerOpen}
      >
        <div className={cx('header')}>
          {isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.6.5') && (
            <Icon size={28} name="close-drawer" onClick={handleForceClose} />
          )}
          <div className={cx['time-text']}>
            <div className={cx('week')}>{time?.split('&')[0]}</div>
            <div className={cx('line')}>|</div>
            <div className={cx('minute')}>{time?.split('&')[1]}</div>
          </div>
        </div>
        <div className={cx('tips-header')}>
          <span>{BODY_PART[currentExposedAreaCodeType]}</span>
          <span>内源热能的操作事项</span>
        </div>
        <div className={cx('drawer-container')}>
          <img src={IMAGE_SRC[currentExposedAreaCodeType]} alt="note" />
        </div>
        <div className={cx('note-text')}>请按图示将衣物脱去，使理疗区域全部露出。</div>
        <Button color="primary" className={cx('btn')} disabled>
          正在检测人体
        </Button>
      </Drawer>
    );
  }
);

export default PhysicalTherapyDrawer;
